import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, FileText } from "lucide-react";
import FiberCanvas from "@/components/FiberCanvas";
import { scrollToId } from "@/lib/site";

const LINES = ["Building the Infrastructure", "Behind Every Connection."];

const CHIPS = [
  { text: "6F | 12F | 24F | 48F | 96F OFC", cls: "right-[6%] top-[24%]", dur: 5.5 },
  { text: "SWITCHES | ROUTERS | SFPS", cls: "right-[20%] top-[48%]", dur: 7 },
  { text: "UG | AERIAL | FIBER INFRASTRUCTURE", cls: "right-[8%] bottom-[16%]", dur: 6.2 },
];

export default function Hero({ onQuote }) {
  const { scrollY } = useScroll();
  const yCanvas = useTransform(scrollY, [0, 900], [0, 170]);
  const fade = useTransform(scrollY, [0, 650], [1, 0.15]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-navy"
    >
      <motion.div style={{ y: yCanvas, opacity: fade }} className="absolute inset-0">
        <FiberCanvas className="h-full w-full" />
      </motion.div>

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/30" />
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-electric/20 blur-[140px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-cyan-500/15 blur-[130px]" />

      {CHIPS.map((c, i) => (
        <motion.div
          key={c.text}
          data-testid={`hero-chip-${i}`}
          className={`pointer-events-none absolute z-10 hidden items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 font-mono text-[11px] font-medium tracking-[0.18em] text-cyan-200/90 backdrop-blur-md lg:inline-flex ${c.cls}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: [0, -10, 0] }}
          transition={{
            opacity: { delay: 1 + i * 0.25, duration: 0.8 },
            y: { delay: 1 + i * 0.25, duration: c.dur, repeat: Infinity, ease: "easeInOut" },
          }}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.9)]" />
          {c.text}
        </motion.div>
      ))}

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-40 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="inline-flex items-center gap-2.5 rounded-full border border-cyan-400/25 bg-cyan-400/10 px-4 py-2"
          data-testid="hero-support-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
          </span>
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-200">
            24/7 Telecom Infrastructure Support | 365 Days
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="mt-8 max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "jtk-text-gradient" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="mt-6 max-w-2xl text-base font-medium text-slate-300 md:text-lg"
        >
          Complete Telecom Infrastructure, Fiber Optic Solutions &amp; Network Equipment
          Supply — From Deployment to Connectivity.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.78 }}
          className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 md:text-base"
        >
          JAYTRIKA delivers reliable telecom equipment, optical fiber infrastructure,
          civil works and connectivity solutions designed to keep networks connected,
          scalable and future-ready.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.92 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-button"
            onClick={() => scrollToId("products")}
            className="group inline-flex items-center gap-2.5 rounded-full bg-electric px-7 py-3.5 text-sm font-semibold text-white shadow-xl shadow-blue-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-500 hover:shadow-blue-500/50"
          >
            Explore Our Solutions
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-quote-button"
            onClick={() => onQuote()}
            className="inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.06] px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-400/40 hover:bg-white/10"
          >
            <FileText size={16} className="text-cyan-300" />
            Request a Quote
          </button>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("about")}
        aria-label="Scroll to about section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-slate-500 transition-colors hover:text-cyan-300"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }} className="block">
          <ChevronDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
}
